"use client";
import React from "react";
import { programsStudentAcademy, usersService } from "../../../../shared/services";
import { ProgramStudentType } from "../../../../types/dto/program";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import type { Resolver } from "react-hook-form";
import * as yup from "yup";
import { message } from "antd";

export type ProgramStudentFormValues = {
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  student_code?: string | null;
  status: ProgramStudentType["status"];
};

export interface UseUpdateProgramStudentProps {
  programStudent: ProgramStudentType;
  setOpenModal: (val: boolean) => void;
  onSuccess?: () => void;
}

export default function useUpdateProgramStudent({
  programStudent,
  setOpenModal,
  onSuccess,
}: UseUpdateProgramStudentProps) {
  const [loading, setLoading] = React.useState(false);

  const {
    control,
    handleSubmit: onSubmit,
    reset,
  } = useForm<ProgramStudentFormValues>({
    resolver: yupResolver(
      yup.object({
        first_name: yup.string().required("Nombre requerido"),
        last_name: yup.string().required("Apellido requerido"),
        email: yup.string().email("Email inválido").required("Email requerido"),
        phone: yup.string().required("Teléfono requerido"),
        student_code: yup.string().nullable(),
        status: yup
          .string()
          .oneOf(["active", "inactive", "pause", "completed"])
          .required("Estado requerido"),
      })
    ) as unknown as Resolver<ProgramStudentFormValues>,
  });

  const handleSubmit = onSubmit(async (data) => {
    if (loading || !programStudent?.user_id) return;
    setLoading(true);

    try {
      await usersService.patch(programStudent.user_id, {
        first_name: data.first_name,
        last_name: data.last_name,
        email: data.email,
        phone: data.phone,
        student_code: data.student_code || null,
      });

      await programsStudentAcademy.patch(programStudent.id, {
        status: data.status,
      });

      message.success("Datos del estudiante actualizados correctamente");
      setOpenModal(false);
      onSuccess?.();
    } catch (err: unknown) {
      const errorMessage =
        err instanceof Error ? err.message : "Error al actualizar el estudiante";
      message.error(errorMessage);
    } finally {
      setLoading(false);
    }
  });

  React.useEffect(() => {
    if (programStudent?.user) {
      reset({
        first_name: programStudent.user.first_name || "",
        last_name: programStudent.user.last_name || "",
        email: programStudent.user.email || "",
        phone: programStudent.user.phone || "",
        student_code: programStudent.user.student_code || "",
        status: programStudent.status,
      });
    }
  }, [programStudent, reset]);

  return {
    control,
    loading,
    handleSubmit,
  };
}
